import React from 'react';
import { useTranslation } from 'localization';
import { Sphere } from 'types/global';
import './PeriodCardAtom.scss';

export interface PeriodCardData {
  key: string;
  name: string;
  startDate: string;
  endDate: string;
  level: string;
  materialized: boolean;
  materializedId?: string;
  hasChildren?: boolean;
}

export interface PeriodCardAtomProps {
  period: PeriodCardData | Sphere;
  chosen: boolean;
  onToggle: (period: PeriodCardData | Sphere) => void;
}

function isPeriodCardData(p: PeriodCardData | Sphere): p is PeriodCardData {
  return 'key' in p && 'materialized' in p;
}

const PeriodCardAtom: React.FC<PeriodCardAtomProps> = ({
  period,
  chosen,
  onToggle,
}) => {
  const { t } = useTranslation();

  const name = isPeriodCardData(period)
    ? period.name
    : (period.properties as Record<string, string>).name;
  const startDate = isPeriodCardData(period)
    ? period.startDate
    : (period.properties as Record<string, string>).startDate;
  const endDate = isPeriodCardData(period)
    ? period.endDate
    : (period.properties as Record<string, string>).endDate;
  const materialized = isPeriodCardData(period) ? period.materialized : true;

  const now = new Date();
  const isCurrent = now >= new Date(startDate) && now <= new Date(endDate);

  const ariaLabel = chosen
    ? t('periodCard.deselect', { name })
    : t('periodCard.select', { name });

  return (
    <button
      type="button"
      className={`period-card-atom${chosen ? ' period-card-atom--selected' : ''}`}
      onClick={() => onToggle(period)}
      aria-pressed={chosen}
      aria-label={ariaLabel}
    >
      <div className="period-card-atom__header">
        <span
          className={`period-card-atom__check${chosen ? ' period-card-atom__check--active' : ''}`}
          aria-hidden="true"
        />
        <span className="period-card-atom__name">{name}</span>
      </div>
      <div className="period-card-atom__dates">
        {new Date(startDate).toLocaleDateString()} –{' '}
        {new Date(endDate).toLocaleDateString()}
      </div>
      <div className="period-card-atom__footer">
        <span
          className={`period-card-atom__dot${materialized ? ' period-card-atom__dot--mat' : ''}`}
          title={materialized ? t('periodCard.materialized') : t('periodCard.virtual')}
          aria-hidden="true"
        >
          {materialized ? '\u25CF' : '\u25CB'}
        </span>
        {isCurrent && (
          <span className="period-card-atom__now">{t('periodCard.now')}</span>
        )}
      </div>
    </button>
  );
};

export default PeriodCardAtom;
